import { AUTH_SOURCE_PARAM_KEY } from '~/modules/root/hook/auth-event-params';

/**
 * Drops an auth gate's persisted open flag when this document is not the one
 * the gate was opened on.
 *
 * The gates keep their open flag in sessionStorage so the modal is still there
 * when a social round-trip's full reload lands back on the page. That made
 * every other new document restore it too: a gate opened on one professional's
 * downloads reappeared on the next professional's page, and on the homepage,
 * still holding the first professional's file and id — so the wrong client
 * would have been credited with the lead.
 *
 * The round-trip is the one navigation a gate may survive, and it says so on
 * the URL. Anything else — a reader who left rather than dismissed, a reload,
 * a shared link — starts with the gate down.
 *
 * Done against storage at module scope rather than in an effect because
 * `atomWithStorage` seeds from its default and syncs from storage afterwards:
 * a component reading `isOpen` on its first commit is told `false` whatever
 * storage holds, so there is no render in which an effect could tell a
 * restored gate from a closed one.
 */
export const discardStaleGateOpen = (
    openKey: string,
    intentKey: string,
    authSource: string,
): void => {
    if (typeof window === 'undefined') return;
    try {
        const resuming =
            new URLSearchParams(window.location.search).get(
                AUTH_SOURCE_PARAM_KEY,
            ) === authSource;
        if (resuming) return;
        window.sessionStorage.removeItem(openKey);
        // The intent belongs to the page it was raised on: it names the file,
        // the professional the lead would be recorded against, and the path it
        // was asked from. Outliving that page is what made it dangerous.
        window.sessionStorage.removeItem(intentKey);
    } catch {
        // Storage blocked (private mode, embedded webview). Nothing persisted,
        // so nothing to discard.
    }
};
